import * as types from './types';
import { uiActions } from 'state/modules/UI';
import * as skusActions from 'services/api/skus';
import { getErrorMessage, parsePagination, isCancel } from 'utils';
import i18n from 'i18n-js';
import { parseInputNumber } from 'utils';

// Get skus
const fetchSkusRequest = () => ({
  type: types.FETCH_SKUS_REQUEST,
});

const fetchSkusSuccess = (payload) => ({
  type: types.FETCH_SKUS_SUCCESS,
  payload,
});

const fetchSkusFailed = () => ({
  type: types.FETCH_SKUS_FAILED,
});

export const fetchSkus = (params = {}, options) => {
  return async (dispatch) => {
    dispatch(fetchSkusRequest());

    const response = await skusActions.fetchSkus(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchSkusSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    if (!isCancel(response)) {
      const error = getErrorMessage(response.data);

      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: error,
        })
      );

      dispatch(fetchSkusFailed(error));
    }
  };
};

// Get sku by ID
const fetchSkuByIdRequest = () => ({
  type: types.FETCH_SKU_BY_ID_REQUEST,
});

const fetchSkuByIdSuccess = (payload) => ({
  type: types.FETCH_SKU_BY_ID_SUCCESS,
  payload,
});

const fetchSkuByIdFailed = () => ({
  type: types.FETCH_SKU_BY_ID_FAILED,
});

export const fetchSkuById = (id, params = {}) => {
  return async (dispatch) => {
    dispatch(fetchSkuByIdRequest());

    const response = await skusActions.fetchSkuById(id, params);

    if (response.ok) {
      return dispatch(
        fetchSkuByIdSuccess({
          data: response.data,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchSkuByIdFailed(error));
  };
};

// Get skus
const fetchDeleteSkuRequest = () => ({
  type: types.FETCH_DELETE_SKU_REQUEST,
});

const fetchDeleteSkuSuccess = (payload) => ({
  type: types.FETCH_DELETE_SKU_SUCCESS,
  payload,
});

const fetchDeleteSkuFailed = () => ({
  type: types.FETCH_DELETE_SKU_FAILED,
});

export const fetchDeleteSku = (id) => {
  return async (dispatch) => {
    dispatch(fetchDeleteSkuRequest());

    const response = await skusActions.fetchDeleteSku(id);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('skus.deleted'),
        })
      );
      return dispatch(fetchDeleteSkuSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchDeleteSkuFailed(error));
  };
};

// Get skus
const fetchUpdateSkuRequest = () => ({
  type: types.FETCH_UPDATE_SKU_REQUEST,
});

const fetchUpdateSkuSuccess = (payload) => ({
  type: types.FETCH_UPDATE_SKU_SUCCESS,
  payload,
});

const fetchUpdateSkuFailed = (errors) => ({
  type: types.FETCH_UPDATE_SKU_FAILED,
  errors,
});

export const fetchUpdateSku = (id, data, handleSuccess) => {
  return async (dispatch) => {
    dispatch(fetchUpdateSkuRequest());

    const response = await skusActions.fetchUpdateSku(id, data);

    if (response.ok) {
      if (handleSuccess) handleSuccess();
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('skus.updated'),
        })
      );
      return dispatch(fetchUpdateSkuSuccess(response.data));
    }

    const formattedError = getErrorMessage(response.data);

    if (typeof formattedError === 'string') {
      dispatch(fetchUpdateSkuFailed(formattedError));

      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(fetchUpdateSkuFailed(errors));
  };
};

const fetchAssociatedTaxRulesetRequest = () => ({
  type: types.FETCH_ASSOCIATED_TAX_RULESET_REQUEST,
});

const fetchAssociatedTaxRulesetSuccess = (payload) => ({
  type: types.FETCH_ASSOCIATED_TAX_RULESET_SUCCESS,
  payload,
});

const fetchAssociatedTaxRulesetFailed = (errors) => ({
  type: types.FETCH_ASSOCIATED_TAX_RULESET_FAILED,
  errors,
});

export const fetchAssociatedTaxRuleset = (id, params = {}) => {
  return async (dispatch) => {
    dispatch(fetchAssociatedTaxRulesetRequest());

    const response = await skusActions.fetchAssociatedTaxRuleset(id, params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchAssociatedTaxRulesetSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAssociatedTaxRulesetFailed(error));
  };
};

// Create sku
const fetchCreateSkuRequest = () => ({
  type: types.FETCH_CREATE_SKU_REQUEST,
});

const fetchCreateSkuSuccess = (payload) => ({
  type: types.FETCH_CREATE_SKU_SUCCESS,
  payload,
});

const fetchCreateSkuFailed = (errors) => ({
  type: types.FETCH_CREATE_SKU_FAILED,
  errors,
});

export const fetchCreateSku = (payload, step = 'creation') => {
  return async (dispatch, getState) => {
    if (step !== 'creation') {
      const {
        onboarding: { position },
      } = getState().skus;
      fetchCreateSkuSuccess(payload);
      return dispatch(onNextPosition({ position: position + 1, step, data: payload }));
    }

    dispatch(fetchCreateSkuRequest());

    const {
      onboarding: { steps },
    } = getState().skus;

    let data = {
      accounting_type_id: steps?.accounting.accounting_type_id,
      active: true,
      auto: payload.automated,
      default_quantity: 1,
      status: 'ACTIVE',
      description: steps?.details.description,
      isRevenue: steps?.accounting?.accounting_type === 'REVENUE' ? true : false,
      manual: payload.manually,
      taxing_choice: steps?.accounting?.taxing_choice || 'DEFAULT',
      name: steps?.details?.name,
      notes: steps?.details?.notes,
      price_unit: steps?.details?.price_unit,
      pricing_type: steps?.details?.tax_type?.name,
      tax_rate_default: steps?.accounting?.tax_rate_default || 0,
      tax_rules_set_id: steps?.accounting?.tax_rules_set_id || undefined,
      type: steps?.accounting?.accounting_type,
    };

    if (payload.default_unit_price) {
      data.default_unit_price = parseInputNumber(payload.default_unit_price);
    }

    const response = await skusActions.fetchCreateSku(data);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('skus.created'),
        })
      );

      return dispatch(fetchCreateSkuSuccess(response.data));
    }

    const formattedError = getErrorMessage(response.data);

    if (typeof formattedError === 'string') {
      dispatch(fetchCreateSkuFailed(formattedError));

      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(fetchCreateSkuFailed(errors));
  };
};

// Onboarding
const onExit = (payload) => ({
  type: types.SET_EXIT,
  payload,
});

export const setExit = (exit, type) => (dispatch) => {
  return dispatch(onExit({ exit, type }));
};

const onNextPosition = (payload) => ({
  type: types.CHANGE_POSITION,
  payload,
});

export const changePosition = (type, exit) => (dispatch, getState) => {
  if (exit) {
    return dispatch(setExit(exit, 'exitOnboarding'));
  }

  const {
    onboarding: { position },
  } = getState().skus;

  const nextPosition = type === 'next' ? 1 : -1;

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

export const changeStep = (step) => (dispatch, getState) => {
  const {
    onboarding: { position },
  } = getState().skus;

  let nextPosition;

  switch (step) {
    case 'details':
      nextPosition = 0;
      break;
    case 'connection':
      nextPosition = 1;
      break;
    default:
      nextPosition = position;
  }

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

// Reset onboarding
export const resetOnboarding = () => (dispatch) => {
  return dispatch({ type: types.RESET_ONBOARDING });
};
